import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import {
  DollarSign,
  Wallet,
  Smartphone,
  CreditCard,
  Check,
  Loader2,
  Info,
  QrCode,
  Copy,
  ExternalLink,
  AlertTriangle,
  Lock,
  ArrowRight
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const PaymentMethodSettings = ({ stripeConnectStatus }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [settings, setSettings] = useState({
    stripe_enabled: true,
    cashapp_enabled: false,
    cashapp_tag: "",
    zelle_enabled: false,
    zelle_email: "",
    zelle_phone: "",
    paypal_enabled: false,
    paypal_email: "",
    cash_enabled: true
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/payments/methods`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        setSettings(prev => ({
          ...prev,
          ...response.data,
          cashapp_enabled: !!response.data.cashapp_tag,
          zelle_enabled: !!(response.data.zelle_email || response.data.zelle_phone),
          paypal_enabled: !!response.data.paypal_email
        }));
      }
    } catch (error) {
      console.error("Failed to fetch payment settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/api/payments/methods`,
        {
          stripe_enabled: settings.stripe_enabled,
          cashapp_tag: settings.cashapp_enabled ? settings.cashapp_tag : null,
          zelle_email: settings.zelle_enabled ? settings.zelle_email : null,
          zelle_phone: settings.zelle_enabled ? settings.zelle_phone : null,
          paypal_email: settings.paypal_enabled ? settings.paypal_email : null,
          cash_enabled: settings.cash_enabled
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Payment settings saved!");
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save payment settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Payment Methods</h3>
          <p className="text-sm text-gray-400">Configure how clients can pay you</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-semibold"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>

      {/* Auto-Track Methods (Stripe) */}
      <Card className={`bg-stone-800/50 border-stone-700 ${!stripeConnectStatus?.payouts_enabled ? 'border-amber-500/50' : ''}`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-400" />
              Card Payments (Stripe)
            </CardTitle>
            {stripeConnectStatus?.payouts_enabled ? (
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                Auto-Tracked
              </Badge>
            ) : (
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                <Lock className="w-3 h-3 mr-1" />
                Setup Required
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Warning if Stripe Connect not set up */}
          {!stripeConnectStatus?.payouts_enabled && (
            <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-400 font-medium text-sm">Stripe Connect Required</p>
                  <p className="text-amber-300/70 text-xs mt-1">
                    You must set up Stripe Connect in the &quot;Fees &amp; Earnings&quot; tab to accept card payments.
                    Without it, this payment method is disabled.
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-300 text-sm">Accept credit/debit cards, Apple Pay, Google Pay</p>
              <p className="text-gray-500 text-xs mt-1">
                {stripeConnectStatus?.payouts_enabled 
                  ? "Payments are automatically confirmed" 
                  : "Complete Stripe Connect setup to enable"}
              </p>
            </div>
            {/* Wrap switch in a div that can receive clicks when disabled */}
            <div 
              onClick={() => {
                if (!stripeConnectStatus?.payouts_enabled) {
                  setShowStripeModal(true);
                }
              }}
              className="cursor-pointer"
            >
              <Switch
                checked={settings.stripe_enabled && stripeConnectStatus?.payouts_enabled}
                onCheckedChange={(checked) => {
                  if (!stripeConnectStatus?.payouts_enabled) {
                    // Show modal with instructions when trying to enable without Stripe Connect
                    setShowStripeModal(true);
                    return;
                  }
                  setSettings({ ...settings, stripe_enabled: checked });
                }}
                className={!stripeConnectStatus?.payouts_enabled ? 'opacity-50' : ''}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Manual Methods Header */}
      <div className="flex items-center gap-2">
        <DollarSign className="w-5 h-5 text-amber-400" />
        <h4 className="text-white font-medium">Pay at Service Methods</h4>
        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">
          Receipt Upload
        </Badge>
      </div>

      <p className="text-gray-400 text-sm -mt-4">
        Clients pay at the time of service and upload a screenshot for verification.
      </p>

      {/* Cash App */}
      <Card className="bg-stone-800/50 border-stone-700">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <div className="w-6 h-6 bg-green-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xs font-bold">$</span>
              </div>
              Cash App
            </CardTitle>
            <Switch
              checked={settings.cashapp_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, cashapp_enabled: checked })}
            />
          </div>
        </CardHeader>
        {settings.cashapp_enabled && (
          <CardContent className="pt-0">
            <div className="space-y-2">
              <Label className="text-gray-300">Your Cash App Tag</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-green-400">$</span>
                  <Input
                    value={settings.cashapp_tag?.replace('$', '') || ''}
                    onChange={(e) => setSettings({ ...settings, cashapp_tag: `$${e.target.value.replace('$', '')}` })}
                    placeholder="YourCashTag"
                    className="bg-stone-900 border-stone-600 text-white pl-7"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">Clients will send payments to this tag</p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Zelle */}
      <Card className="bg-stone-800/50 border-stone-700">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <div className="w-6 h-6 bg-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white text-xs font-bold">Z</span>
              </div>
              Zelle
            </CardTitle>
            <Switch
              checked={settings.zelle_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, zelle_enabled: checked })}
            />
          </div>
        </CardHeader>
        {settings.zelle_enabled && (
          <CardContent className="pt-0 space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Zelle Email</Label>
              <Input
                type="email"
                value={settings.zelle_email || ''}
                onChange={(e) => setSettings({ ...settings, zelle_email: e.target.value })}
                placeholder="your@email.com"
                className="bg-stone-900 border-stone-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Or Phone Number</Label>
              <Input
                type="tel"
                value={settings.zelle_phone || ''}
                onChange={(e) => setSettings({ ...settings, zelle_phone: e.target.value })}
                placeholder="(555) 123-4567"
                className="bg-stone-900 border-stone-600 text-white"
              />
            </div>
            <p className="text-xs text-gray-500">Provide at least one: email or phone</p>
          </CardContent>
        )}
      </Card>

      {/* PayPal */}
      <Card className="bg-stone-800/50 border-stone-700">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <div className="w-6 h-6 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white text-xs font-bold">P</span>
              </div>
              PayPal
            </CardTitle>
            <Switch
              checked={settings.paypal_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, paypal_enabled: checked })}
            />
          </div>
        </CardHeader>
        {settings.paypal_enabled && (
          <CardContent className="pt-0">
            <div className="space-y-2">
              <Label className="text-gray-300">PayPal Email</Label>
              <Input
                type="email"
                value={settings.paypal_email || ''}
                onChange={(e) => setSettings({ ...settings, paypal_email: e.target.value })}
                placeholder="your@paypal.com"
                className="bg-stone-900 border-stone-600 text-white"
              />
              <p className="text-xs text-gray-500">Clients will send payments to this email</p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Cash at Salon */}
      <Card className="bg-stone-800/50 border-stone-700">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-400" />
              Cash at Salon
            </CardTitle>
            <Switch
              checked={settings.cash_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, cash_enabled: checked })}
            />
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-gray-400 text-sm">Accept cash payments when clients arrive</p>
        </CardContent>
      </Card>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-blue-400 font-medium text-sm">How Receipt Verification Works</h4>
            <p className="text-gray-300 text-xs mt-1">
              When clients pay via Cash App, Zelle, or PayPal, they&apos;ll upload a screenshot of their payment. 
              You&apos;ll receive a notification to verify the receipt and confirm the appointment.
            </p>
          </div>
        </div>
      </div>

      {/* Stripe Connect Required Modal */}
      <Dialog open={showStripeModal} onOpenChange={setShowStripeModal}>
        <DialogContent className="bg-stone-900 border-stone-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-400" />
              Stripe Connect Required
            </DialogTitle>
            <DialogDescription className="text-stone-400">
              To accept credit/debit card payments, you need to set up Stripe Connect first.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-300 font-medium text-sm">One-time setup required</p>
                  <p className="text-amber-200/70 text-xs mt-1">
                    Stripe Connect allows you to receive payments directly to your bank account. This is required to accept card payments.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-white font-medium text-sm">How to set up:</h4>
              <ol className="space-y-2 text-sm text-stone-300">
                <li className="flex items-start gap-2">
                  <span className="bg-amber-500 text-black rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">1</span>
                  <span>Go to the <strong className="text-amber-400">&quot;Fees &amp; Earnings&quot;</strong> tab in your dashboard</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-amber-500 text-black rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">2</span>
                  <span>Click <strong className="text-amber-400">&quot;Set Up Stripe Connect&quot;</strong> button</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-amber-500 text-black rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">3</span>
                  <span>Complete the Stripe onboarding process (takes ~5 minutes)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-amber-500 text-black rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">4</span>
                  <span>Return here to enable card payments</span>
                </li>
              </ol>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowStripeModal(false)}
              className="border-stone-700 text-stone-300"
            >
              Maybe Later
            </Button>
            <Button
              onClick={() => {
                setShowStripeModal(false);
                // Navigate to Fees & Earnings tab - using window event
                window.dispatchEvent(new CustomEvent('navigate-tab', { detail: 'fees' }));
              }}
              className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-semibold"
            >
              Go to Fees & Earnings
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentMethodSettings;
